import { DatePipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { filter, switchMap } from 'rxjs';
import { ShareReferenceType, ShareType, Uuid } from '../../../../graphql';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { LocalizePath } from '../../../utils/types/localize-path';
import { DigitalSignatureApiService } from '../domain/api/digital-signature-list.api.service';
import { GetDigitalSignatureWithFilesResult } from '../domain/api/query/GetDigitalSignatures.generated';

type DigitalSignature = NonNullable<NonNullable<GetDigitalSignatureWithFilesResult>['shareByFilter']>;
@Component({
    selector: 'app-digital-signature-viewer',
    imports: [DatePipe, LowerCasePipe],
    templateUrl: './digital-signature-viewer.component.html',
    styleUrls: [
        '../../../../../src/app/shared/composable-styles/side-form.scss',
        '../../../../../src/app/shared/composable-styles/button.scss',
        '../../../../../src/app/shared/composable-styles/card.scss',
        '../../../../../src/app/shared/composable-styles/text.scss',
        '../../../../../src/app/shared/composable-styles/table.scss',
        './digital-signature-viewer.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitalSignatureViewerComponent implements OnInit {
    #api = inject(DigitalSignatureApiService);
    #model = inject(BsModalRef);
    #logger = inject(BxLogger);
    #sanitizer = inject(DomSanitizer);
    #translate = inject(TranslateService);
    initialShareId?: Uuid;
    shareReferenceType?: ShareReferenceType;
    #shareId = signal(null as null | Uuid);
    dateTimeFormat = inject(TranslateService).instant('general.dateTimeFormat' satisfies LocalizePath);
    share = signal(null as null | DigitalSignature);
    docType = 'Quote';
    safePdfUrl = computed(() => {
        const url = this.share()?.digitalSignature?.signedDocumentUrl;
        if (url) return this.#sanitizer.bypassSecurityTrustResourceUrl(url);
    });
    #digitalSigApiSub = toObservable(this.#shareId)
        .pipe(
            filter((x): x is NonNullable<typeof x> => !!x),
            switchMap((shareId) => this.#api.getDigitalSignatureWithFiles({ shareId, shareReferenceType: this.shareReferenceType })),
            takeUntilDestroyed()
        )
        .subscribe((x) => {
            const share = x.data.shareByFilter;
            if (!share) return;
            if (share?.type === ShareType.Variation) {
                this.docType = this.#translate.instant('general.variation');
            }
            this.share.set(share);
        });

    ngOnInit(): void {
        if (!this.initialShareId) throw new Error('initialShareId is required for DigitalSignatureViewerComponent');
        if (!this.shareReferenceType) throw new Error('shareReferenceType is required for DigitalSignatureViewerComponent');
        this.#shareId.set(this.initialShareId);
    }

    close() {
        this.#model.hide();
    }
}
